import { Navigate } from "react-router-dom";
import { useCompanyCapabilities } from "../../../hooks/useCompanyCapabilities";
import { getRequiredCapabilityForPath } from "../../../utils/investorPortalRouteCapabilities";
import ModuleUnavailablePage from "./ModuleUnavailablePage";
import "./investorPortalAdmin.css";

/**
 * Guards staff investor-portal routes by company capability flags.
 * Sidebar hiding alone is not sufficient — this blocks direct URL access.
 */
export default function InvestorPortalRoute({ children, requireCapability }) {
  const caps = useCompanyCapabilities();
  const required = requireCapability || null;

  if (!caps.hasAnyInvestorFeature && !caps.investorPortal) {
    return <ModuleUnavailablePage moduleName="Investor modules" />;
  }

  if (required === "investment" && !caps.investment) {
    return <ModuleUnavailablePage moduleName="Investment Management" />;
  }

  if (required === "marketplace" && !caps.marketplace) {
    return <ModuleUnavailablePage moduleName="Marketplace Admin" />;
  }

  if (required === "cooperativeRequest") {
    // Cooperative service-request queue retired — membership is /member-signup.
    return <Navigate to="/super_admin/investor_portal/requests/real-estate" replace />;
  }

  if (required === "realEstate" && !caps.realEstate) {
    return <ModuleUnavailablePage moduleName="Real Estate Requests" />;
  }

  if (required === "serviceRequests" && !caps.realEstate) {
    return <ModuleUnavailablePage moduleName="Service Requests" />;
  }

  if (required === "investorPortal" && !caps.investorPortal) {
    return <ModuleUnavailablePage moduleName="Investor Management" />;
  }

  return children;
}

/** HOC-style guard using current pathname */
export function InvestorPortalPathGuard({ children, pathname }) {
  const caps = useCompanyCapabilities();
  const requiredKey = getRequiredCapabilityForPath(pathname);

  if (!requiredKey) {
    return children;
  }

  if (requiredKey === "serviceRequests" && !caps.realEstate) {
    return <ModuleUnavailablePage moduleName="Service Requests" />;
  }

  if (!caps.capabilities[requiredKey]) {
    if (requiredKey === "investment") {
      return <ModuleUnavailablePage moduleName="Investment Management" />;
    }
    if (requiredKey === "marketplace") {
      return <ModuleUnavailablePage moduleName="Marketplace Admin" />;
    }
    if (requiredKey === "cooperativeRequest") {
      return <Navigate to="/super_admin/investor_portal/requests/real-estate" replace />;
    }
    if (requiredKey === "realEstate") {
      return <ModuleUnavailablePage moduleName="Real Estate Requests" />;
    }
    return <Navigate to="/super_admin/dashboard" replace />;
  }

  if (!caps.hasAnyInvestorFeature && !caps.investorPortal) {
    return <ModuleUnavailablePage moduleName="Investor modules" />;
  }

  return children;
}
